import { ShortcutItem } from "@repo/core/components/core/Shortcuts/types";
import { SalesOptionCardProps } from "./components/SalesOptionCard/types";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { ROUTES } from "@repo/core/routes";

import { ScrollView as RNScrollView } from "react-native";
import { useRef, useState } from "react";

import { SalesSummaryCardProps } from "./components/SalesSummaryCard/types";
import { useQuery } from "@tanstack/react-query";
import { TransactionsResource } from "@repo/core/services/resources/transactions";
import { apiInstance } from "@repo/core/services/instances/api";
import { formatDate } from "@repo/core/utils/date.utils";
import { GroupedTransactionStatusEnum } from "@repo/core/constants/transactions";
import { useBreakpointValue } from "@gluestack-style/react";
import { useStoresAndShopkeepersStore } from "@repo/core/stores/storesAndShopkeepers";
import { useSelectStore } from "@repo/core/hooks/useSelectStore";
import { useNewTransactionFlow } from "@repo/core/hooks/useNewTransactionFlow";
import { TransactionFlowType } from "@repo/core/screens/NewTransaction/types";

const transactionsResource = new TransactionsResource(apiInstance);

export const SalesController = () => {
  const { push } = useNavigation();
  const isResponsive = useBreakpointValue({
    base: true,
    sm: true,
    md: false
  });
  const { isTablet, isWeb } = useDeviceType();
  const isTabletOrWeb = isTablet || isWeb;
  const {
    selectedStore,
    selectedShopkeeper,
    hasMoreThanOneShopkeeper,
    hasMoreThanOneStore
  } = useStoresAndShopkeepersStore();

  const { startNewTransactionFlow } = useNewTransactionFlow();
  const { modalRef, handleOnSelectStore } = useSelectStore({});

  const { data: transactionsData, isLoading: transactionsIsLoading } = useQuery(
    {
      queryKey: ["transactionsResume"],
      queryFn: () => transactionsResource.transactionsResume()
    }
  );

  const transactions = transactionsData?.data;

  const refScroll = useRef<RNScrollView | undefined>(undefined);

  const [currentPosition, setCurrentPosition] = useState(0);

  const salesSumaryItems: SalesSummaryCardProps[] = [
    {
      title: "Compras e Saques",
      date: formatDate(new Date(transactions?.datDataAtual || Date.now())),
      type: "success",
      value: transactions?.objTransacoesConfirmadas.dblValor || 0,
      totalTransactions:
        transactions?.objTransacoesConfirmadas.intTransacoes || 0,
      onPress: () =>
        push(ROUTES.transactions, {
          transactionsStatus: GroupedTransactionStatusEnum.CONCLUIDO
        })
    },
    {
      title: "Transações Canceladas",
      date: formatDate(new Date(transactions?.datDataAtual || Date.now())),
      type: "cancelled",
      value: transactions?.objTransacoesCanceladas.dblValor || 0,
      totalTransactions:
        transactions?.objTransacoesCanceladas.intTransacoes || 0,
      onPress: () =>
        push(ROUTES.transactions, {
          transactionsStatus: GroupedTransactionStatusEnum.CANCELADO
        })
    },
    {
      title: "Transações Pendentes",
      date: formatDate(new Date(transactions?.datDataAtual || Date.now())),
      type: "pending",
      value: transactions?.objTransacoesPendentes.dblValor || 0,
      totalTransactions:
        transactions?.objTransacoesPendentes.intTransacoes || 0,
      onPress: () =>
        push(ROUTES.transactions, {
          transactionsStatus: GroupedTransactionStatusEnum.PENDENTE
        })
    }
  ];

  const TRANSACTIONS_SHORTCUTS: ShortcutItem[] = [
    {
      onPress: () => {
        startNewTransactionFlow(TransactionFlowType.PURCHASE);
      },
      icon: "credit-card",
      label: `Compra \n no Crédito`,
      type: "primary"
    },
    {
      onPress: () => push(ROUTES.invoicePaymentOptions),
      icon: "file-invoice-dollar",
      label: "Pagamentos",
      type: "primary"
    },
    {
      onPress: () => {
        startNewTransactionFlow(TransactionFlowType.FIDELITY_SCORE);
      },
      icon: "star",
      label: "Pontuar Fidelidade",
      type: "primary"
    },
    {
      onPress: () => {
        startNewTransactionFlow(TransactionFlowType.BONUS_RETRIEVAL);
      },
      icon: "tag",
      label: "Utilizar Bônus",
      type: "primary"
    }
  ];
  const OTHER_OPERATIONS_SHORTCUTS: ShortcutItem[] = [
    {
      icon: "bars-progress",
      label: `Consultar \n Limites`,
      type: "secondary",
      onPress: () => push(ROUTES.consultLimitsVerifyIdentity)
    },
    {
      icon: "calendars",
      label: `Simular \n Parcelamento`,
      type: "secondary",
      onPress: () => push(ROUTES.simulateInstallmentsOptions)
    },
    {
      icon: "clipboard-check",
      label: `Fechamento \n do dia`,
      type: "secondary",
      onPress: () => push(ROUTES.closingOfTheDay)
    },
    {
      icon: "receipt",
      label: `2° Via de \n Comprovante`,
      type: "secondary",
      onPress: () => push(ROUTES.selectSecondCopyOfReceipt)
    }
  ];

  const MENU_OPTIONS: SalesOptionCardProps[] = [
    {
      name: "Transações do dia",
      description: "Transações realizadas neste terminal hoje",
      onPress: () => push(ROUTES.transactions)
    },
    {
      name: "Saque Emergencial",
      description: "Retirada em espécie via cartão de crédito",
      onPress: () => {
        startNewTransactionFlow(TransactionFlowType.EMERGENCY_WITHDRAW);
      }
    },
    {
      name: "Cancelamentos",
      description: "Cancelar compras, pagamentos, pontuação",
      onPress: () => push(ROUTES.cancelTransactionOptions)
    }
  ];

  const goLeft = () => {
    if (refScroll.current) {
      const newPosition = currentPosition - 400;
      refScroll?.current?.scrollTo({
        x: newPosition,
        animated: true
      });
    }
  };

  const goRight = () => {
    if (refScroll.current) {
      const newPosition = currentPosition + 400;
      refScroll?.current?.scrollTo({
        x: newPosition,
        animated: true
      });
    }
  };
  const enableSelectStore = hasMoreThanOneStore || hasMoreThanOneShopkeeper;

  return {
    transactionsIsLoading,
    goLeft,
    goRight,
    setCurrentPosition,
    isResponsive,
    refScroll,
    TRANSACTIONS_SHORTCUTS,
    OTHER_OPERATIONS_SHORTCUTS,
    MENU_OPTIONS,
    salesSumaryItems,
    isTabletOrWeb,
    selectedStore,
    selectedShopkeeper,
    modalRef,
    handleOnSelectStore,
    enableSelectStore
  };
};
